/* eslint-disable max-lines-per-function */
import { Icon16ChevronOutline, Icon20ChevronRightOutline, Icon24Done } from '@vkontakte/icons';
import classNames from 'clsx';
import { ReactComponent as FolderIcon } from 'img/icons/folder.svg';
import { ReactComponent as FolderFadeIcon } from 'img/icons/folderFade.svg';
import { ReactComponent as FolderPeopleIcon } from 'img/icons/folderPeople.svg';
import { equals } from 'ramda';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { IS_BIZ_USER } from 'reactApp/appHelpers/configHelpers';
import { ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { setDomainFoldersFilterActive } from 'reactApp/modules/home/home.actions';
import { CloudItem, EStorageType } from 'reactApp/modules/storage/storage.types';
import { DropNotify } from 'reactApp/ui//DropNotify/DropNotify';
import { Hint } from 'reactApp/ui/Hint/Hint';
import { Link } from 'reactApp/ui/Link/Link';
import { ETreeNodeMode, ETreeRootIds } from 'reactApp/ui/TreeComponent/TreeComponent.constants';
import { checkForceOpenFoldersOnThePath } from 'reactApp/ui/TreeComponent/TreeComponent.helpers';
import { ITreeNodeClickData, TreeNodeData } from 'reactApp/ui/TreeComponent/TreeNode.types';
import { sendDwh, sendXray } from 'reactApp/utils/ga';
import { noop } from 'reactApp/utils/helpers';
import { ECategoryGa } from 'reactApp/utils/paymentGa';

import { useDragAndDrop } from './hooks/useDragAndDrop';
import styles from './TreeNode.css';

interface Props {
    selectedId: string;
    selectedStorage?: EStorageType;
    mod?: ETreeNodeMode;
    data: TreeNodeData;
    onExpand: ({ id, gaId, storage, isOpen, level, selectedStorage }) => Promise<void>;
    onClick: (data: ITreeNodeClickData) => void;
    level?: number;
    forceOpen?: boolean;
    isMobile?: boolean;
    // Ref на выбранный элемент
    handleScrollIntoViewRef?: (RefObject) => void;
    maxTreeLevel?: number;
    search?: string;
    domainFoldersFilterActive?: boolean;
    isParentSharedOrMounted?: boolean;
    isParentPublic?: boolean;
    isDragging: boolean;
    itemsToDrop: (CloudItem | undefined)[];
    // tempexp_14978-next-line
    isNewLeftMenuFeatureEnabled?: boolean;
}

const areEqual = (prevProps: Props, nextProps: Props): boolean =>
    prevProps.selectedId === nextProps.selectedId &&
    prevProps.search === nextProps.search &&
    prevProps.data.itemsCounter === nextProps.data.itemsCounter &&
    prevProps.isDragging === nextProps.isDragging &&
    prevProps.isParentSharedOrMounted === nextProps.isParentSharedOrMounted &&
    prevProps.isParentPublic === nextProps.isParentPublic &&
    prevProps.itemsToDrop?.length === nextProps.itemsToDrop?.length &&
    equals(prevProps.data, nextProps.data);

const getTreeMargin = (level: number) => `${22 * level}px`;

const iconComponentMap = {
    mountFolder: <FolderPeopleIcon width={16} height={16} data-qa-id="mountFolder" />,
    folder: <FolderIcon width={16} height={16} data-qa-id="folder" />,
};

/* eslint-disable complexity */
export const TreeNode = memo<Props>(
    ({
        data,
        selectedId,
        mod,
        onExpand = () => Promise.resolve(),
        onClick = noop,
        level = 0,
        forceOpen = false,
        isMobile = false,
        selectedStorage = EStorageType.home,
        handleScrollIntoViewRef = noop,
        maxTreeLevel,
        search = '',
        domainFoldersFilterActive,
        isParentSharedOrMounted = false,
        isParentPublic = false,
        isDragging = false,
        itemsToDrop,
        // tempexp_14978-next-line
        isNewLeftMenuFeatureEnabled,
    }) => {
        const dispatch = useDispatch();
        const {
            id,
            gaId,
            folderCounter,
            title,
            children,
            itemsCounter,
            icon,
            storage,
            href,
            component,
            isMountedOrShared,
            // tempexp_14978-start
            divider,
            isFolder,
            hideIcon,
            // tempexp_14978-end
        } = data;
        const isSharedOrMounted = isMountedOrShared || isParentSharedOrMounted;
        const isThisOrParentPublic = Boolean(isParentPublic || data.isPublic);

        const forceOpenFoldersOnThePath = checkForceOpenFoldersOnThePath(id, selectedStorage, selectedId, children, mod);

        const [isOpen, setOpen] = useState<boolean | null>(forceOpen || forceOpenFoldersOnThePath || null);
        const [isLoading, setLoading] = useState(false);

        const levelMargin = getTreeMargin(level);
        const levelMarginNext = getTreeMargin(level + 1);
        const isExpandable = folderCounter;
        const isSelected = domainFoldersFilterActive && id === '/domain' ? true : storage + id === selectedId;

        const { isDraggingOver, handleMouseMove, handleMouseLeave, handleStopDrag, isAllowedToDrop } = useDragAndDrop(
            data,
            isSelected,
            isDragging,
            itemsToDrop
        );

        // В этом и нижележащих компонентах, в том числе хуках, нельзя лазить в стор!
        // Иначе тут очень много перерендеров при массивных/частых обновлениях стора
        // Все из стора прокидывать снаружи из TreeComponent (либо в data из TreeComponent.helpers)
        // Если надо, то не забывать добавлять новые свойства в ф-ию areEqual выше

        const isSelectDialog = mod === ETreeNodeMode.selectDlg || mod === ETreeNodeMode.selectCheck || mod === ETreeNodeMode.albumsDlg;
        const folderType = isMountedOrShared ? 'mountFolder' : 'folder';

        const loaderItems =
            (isOpen || forceOpenFoldersOnThePath) && isLoading && folderCounter ? Array.from(new Array(folderCounter).keys()) : null;

        const noLink = id === ETreeRootIds.domain || isSelectDialog;

        const toggleDomainFoldersFilter = useCallback(() => {
            if (!IS_BIZ_USER || isSelectDialog) {
                return;
            }

            if (id !== '/domain' || level > 0) {
                return dispatch(setDomainFoldersFilterActive(false));
            }

            dispatch(setDomainFoldersFilterActive(true));
        }, [dispatch, id, level, isSelectDialog]);
        const treeNodeRef = useRef<HTMLDivElement | null>(null);

        const handleExpand = useCallback(
            (e?: React.MouseEvent<HTMLElement>, forceOpen?: boolean) => {
                e?.stopPropagation();

                if (maxTreeLevel && level >= maxTreeLevel) {
                    return;
                }

                const newIsOpen = forceOpen ? true : !isOpen;

                setOpen(newIsOpen);

                if (newIsOpen) {
                    setLoading(true);
                }

                onExpand({ isOpen: newIsOpen, id, storage, gaId, level, selectedStorage }).finally(() => {
                    setLoading(false);
                });
            },
            [maxTreeLevel, id, gaId, storage, level, isOpen, setOpen, setLoading, selectedStorage, onExpand]
        );

        useEffect(() => {
            const folderToSelected = selectedId.startsWith(`${storage}${id}/`);
            const childrensLoaded = children?.length;
            if ((isOpen && !childrensLoaded) || (folderToSelected && !childrensLoaded && !isOpen)) {
                handleExpand(undefined, true);
            }
            if (folderToSelected && childrensLoaded && !isOpen) {
                setOpen(true);
            }
        }, [selectedId]);

        const handleClick = useCallback(
            (e: React.MouseEvent<HTMLElement>) => {
                e.stopPropagation();
                toggleDomainFoldersFilter();
                if (!isOpen && isExpandable) {
                    handleExpand(e);
                }

                if (storage !== EStorageType.home || (storage === EStorageType.home && id === ROOT_FOLDER_ID)) {
                    sendDwh({
                        eventCategory: ECategoryGa.entered,
                        action: 'section',
                        dwhData: {
                            section: storage,
                        },
                    });
                }
            },
            [isOpen, id, storage, isExpandable, handleExpand, toggleDomainFoldersFilter]
        );

        const handleGoTo = useCallback(
            (e: React.MouseEvent<HTMLElement>) => {
                e.stopPropagation();
                const newId = id === '/domain' ? '/' : id;

                if (!isSelectDialog) {
                    toggleDomainFoldersFilter();
                }

                onClick({ id: newId, gaId, storage, href, level, isSharedOrMounted, isThisOrParentPublic });

                if (!isOpen && isExpandable) {
                    handleExpand(e);
                }
            },
            [
                id,
                gaId,
                href,
                storage,
                level,
                isOpen,
                isSharedOrMounted,
                isThisOrParentPublic,
                onClick,
                handleExpand,
                isExpandable,
                toggleDomainFoldersFilter,
                isSelectDialog,
            ]
        );

        const handleDoubleClick = useCallback(
            (e: React.MouseEvent<HTMLElement>) => {
                e.stopPropagation();

                onClick({ id, gaId, storage, href, level, isSharedOrMounted, isThisOrParentPublic });

                if (isExpandable) {
                    handleExpand(e);
                }
            },
            [id, gaId, href, storage, level, onClick, isExpandable, isSharedOrMounted, isThisOrParentPublic, handleExpand]
        );

        const handleExpandClick = useCallback(
            (e: React.MouseEvent<HTMLElement>) => {
                e.preventDefault();
                handleExpand(e);
            },
            [handleExpand]
        );

        useEffect(() => {
            if (!loaderItems) {
                sendXray([`${isSelectDialog ? 'dlg' : 'lm'}-tree-folder`, 'show', level.toString(), (!!isExpandable).toString()]);
            }
        }, [loaderItems, level, maxTreeLevel, isExpandable, mod]);

        useEffect(() => {
            if (isSelected && treeNodeRef.current) {
                handleScrollIntoViewRef(treeNodeRef.current);
            }
        }, [handleScrollIntoViewRef, isSelected, treeNodeRef.current]);

        const handleRef = useCallback((ref: HTMLDivElement) => {
            treeNodeRef.current = ref;
        }, []);

        if (maxTreeLevel && level > maxTreeLevel) {
            return null;
        }

        const renderChildren = () => {
            if (component) {
                return <>{component}</>;
            }

            return loaderItems
                ? loaderItems.map((item) => (
                      <div className={classNames(styles.item, styles.loaderItem)} key={item}>
                          <div className={styles.space} style={{ width: levelMarginNext }} />
                          <div className={styles.expandControl} />
                          <div className={styles.folderIcon}>
                              <FolderFadeIcon width={20} height={20} />
                          </div>
                          <div className={styles.titlePlaceholder} />
                      </div>
                  ))
                : !!children &&
                      children.map((item) => (
                          <TreeNode
                              key={item.id}
                              selectedId={selectedId}
                              data={item}
                              mod={mod}
                              search={search}
                              onExpand={onExpand}
                              onClick={onClick}
                              level={level + 1}
                              selectedStorage={selectedStorage}
                              handleScrollIntoViewRef={handleScrollIntoViewRef}
                              maxTreeLevel={maxTreeLevel}
                              isParentSharedOrMounted={isSharedOrMounted}
                              isParentPublic={isThisOrParentPublic}
                              itemsToDrop={itemsToDrop}
                              isDragging={isDragging}
                              // tempexp_14978-next-line
                              isNewLeftMenuFeatureEnabled={isNewLeftMenuFeatureEnabled}
                          />
                      ));
        };

        return (
            <>
                <div className={classNames({ [styles.root]: true, [styles[`root_${mod}`]]: !!mod })} data-name={id}>
                    <Link
                        id={id}
                        href={href}
                        storage={storage}
                        handleGoTo={handleGoTo}
                        handleClick={handleClick}
                        noLink={noLink}
                        search={search}
                    >
                        <div
                            className={classNames(styles.item, {
                                [styles.selected]: isSelected,
                                [styles.drop]: !isSelectDialog && isDraggingOver && isAllowedToDrop,
                                [styles.dropDelete]: !isSelectDialog && isDraggingOver && isAllowedToDrop && id === ETreeRootIds.trashbin,
                            })}
                            onDoubleClick={isSelectDialog && !isMobile ? handleDoubleClick : noop}
                            ref={handleRef}
                            onMouseMove={isSelectDialog ? noop : handleMouseMove}
                            onMouseLeave={isSelectDialog ? noop : handleMouseLeave}
                            onMouseUp={isSelectDialog ? noop : handleStopDrag}
                            onMouseOver={!isSelectDialog && isExpandable && isDraggingOver && !isOpen ? handleExpand : noop}
                            data-id={id}
                        >
                            <div className={styles.space} style={{ width: levelMargin }} />
                            <div
                                className={classNames(styles.expandControl, {
                                    [styles.expandControlExpandable]: isExpandable,
                                    // tempexp_14978-start
                                    [styles.hideExpandControl]: isNewLeftMenuFeatureEnabled && !isExpandable && !isFolder,
                                    [styles.expandControlNew]: isNewLeftMenuFeatureEnabled,
                                    // tempexp_14978-end
                                })}
                                onClick={isExpandable ? handleExpandClick : noop}
                            >
                                {/* tempexp_14978-next-start */}
                                {Boolean(folderCounter) &&
                                    (isNewLeftMenuFeatureEnabled ? (
                                        <Icon20ChevronRightOutline
                                            className={classNames(styles.expandIcon, {
                                                [styles.expandIconExpanded]:
                                                    (!maxTreeLevel || level < maxTreeLevel) &&
                                                    (isOpen || (forceOpenFoldersOnThePath && isOpen !== false)),
                                            })}
                                        />
                                    ) : (
                                        <Icon16ChevronOutline
                                            className={classNames(styles.expandIcon, {
                                                [styles.expandIconExpanded]:
                                                    (!maxTreeLevel || level < maxTreeLevel) &&
                                                    (isOpen || (forceOpenFoldersOnThePath && isOpen !== false)),
                                            })}
                                        />
                                    ))}
                                {/* tempexp_14978-next-end */}
                            </div>
                            {/* tempexp_14978-next-line */}
                            {!hideIcon && <div className={styles.folderIcon}>{icon || iconComponentMap[folderType]}</div>}
                            <Hint text={level > 0 && !isDraggingOver ? title : ''}>
                                <div className={styles.title}>{title}</div>
                            </Hint>
                            {!!itemsCounter && (
                                <div className={styles.counter}>
                                    <div className={styles.counterVal}>{itemsCounter}</div>
                                </div>
                            )}
                            {mod === ETreeNodeMode.selectCheck && isSelected && (
                                <div className={styles.check}>
                                    <Icon24Done />
                                </div>
                            )}

                            {!isSelectDialog && isDraggingOver && (
                                <DropNotify
                                    dragOverName={title}
                                    isAllowedToDrop={Boolean(isAllowedToDrop)}
                                    target={treeNodeRef}
                                    specialText={storage === EStorageType.trashbin ? 'Удалить' : undefined}
                                />
                            )}
                        </div>
                    </Link>
                    <div className={styles.children}>{(isOpen || (forceOpenFoldersOnThePath && isOpen !== false)) && renderChildren()}</div>
                </div>
                {/* tempexp_14978-next-line */}
                {divider && isNewLeftMenuFeatureEnabled && <div className={styles.divider} />}
            </>
        );
    },
    areEqual
);

TreeNode.displayName = 'TreeNode';

TreeNode.whyDidYouRender = true;
